<template>
  <div
    class="border mb-6 shadow-lg border-primary-50 bg-white dark:border-dark-400 dark:bg-dark-500"
    style="border-radius: 1rem"
  >
    <div
      class="px-3 py-3 font-bold border-b bg-primary border-primary-50 text-white dark:bg-dark-400 dark:border-dark-400 dark:text-dark-text"
      style="border-radius: 1rem 1rem 0 0"
    >
      <div class="flex">
        <span class="mx-1 text-lg"> {{ $t('message.RESULT') }} :</span>
        <judge-badge v-if="judgeRecord && !isJudgeLoading" class="my-auto" :status="judgeRecord.status" />
      </div>
    </div>
    <div class="border-b border-gray-300 dark:border-dark-400">
      <div
        v-if="isJudgeLoading"
        class="relative h-16 p-4 text-gray-500 bg-white dark:text-dark-text dark:bg-dark-500"
        :class="isLightMode() ? 'loading' : 'loading-dark'"
      >
        {{ $t('message.loading') }}
      </div>
      <JudgeStep v-else :currentJudgeStep="currentJudgeStep" :judgeStepList="judgeStepList" />
    </div>
    <div
      class="grid grid-cols-4 border-b h-8 text-base font-bold text-gray-800 bg-white dark:text-dark-text dark:bg-dark-500 dark:border-dark-400"
    >
      <div
        class="border-r flex justify-center items-center bg-primary-300 text-white border-primary-200 dark:border-dark-400 dark:bg-dark-400"
      >
        {{ $t('message.lang') }}
      </div>
      <div
        class="border-r flex justify-center items-center bg-primary-300 text-white border-primary-200 dark:border-dark-400 dark:bg-dark-400"
      >
        {{ $t('message.time') }}
      </div>
      <div
        class="border-r flex justify-center items-center bg-primary-300 text-white border-primary-200 dark:border-dark-400 dark:bg-dark-400"
      >
        {{ $t('message.memory') }}
      </div>
      <div
        class="flex justify-center items-center bg-primary-300 text-white border-primary-200 dark:border-dark-400 dark:bg-dark-400"
      >
        {{ $t('message.code') }}
      </div>
    </div>

    <div
      v-if="isJudgeLoading"
      class="h-8 px-4 mt-2 text-center text-gray-800 bg-white dark:text-dark-text dark:bg-dark-500"
      style="border-radius: 0 0 1rem 1rem"
    >
      {{ $t('message.loading') }}
    </div>
    <div
      v-else-if="!judgeRecord"
      class="h-8 px-4 mt-2 text-center text-gray-800 bg-white dark:text-dark-text dark:bg-dark-500"
      style="border-radius: 0 0 1rem 1rem"
    >
      {{ $t('message.noData') }}
    </div>
    <div
      v-else
      class="grid grid-cols-4 h-8 text-base text-gray-800 bg-white dark:text-dark-text dark:bg-dark-500 dark:border-dark-400"
      style="border-radius: 0 0 1rem 1rem"
    >
      <div class="border-r flex justify-center items-center dark:border-dark-400">
        {{ judgeRecord.programLanguage }}
      </div>
      <div class="border-r flex justify-center items-center dark:border-dark-400">
        {{ (judgeRecord.cpuTime / 100).toFixed(2) }} sec
      </div>
      <div class="border-r flex justify-center items-center dark:border-dark-400"> {{ judgeRecord.memory }} KB </div>
      <div class="flex justify-center items-center dark:border-dark-400"> {{ judgeRecord.codeSize }} B </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'

import JudgeStep from './JudgeStep.vue'

import JudgeBadge from '@/presentation/components/Text/JudgeBadge.vue'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useJudgeDetailPresenter } from '@/presentation/composables/judge/useJudgeDetailPresenter'
import { useJudgeStatePresenter } from '@/presentation/composables/judge/useJudgeStatePresenter'

export default defineComponent({
  components: {
    JudgeStep,
    JudgeBadge
  },
  setup(props) {
    const { isLightMode } = useConfigStatePresenter()
    const { judgeDetail } = useJudgeDetailPresenter()
    const { isJudgeLoading } = useJudgeStatePresenter()
    const judgeRecord = computed(() => (judgeDetail.value ? judgeDetail.value.submissionRecord : null))

    const judgeStepList = [
      'Submitted',
      'Sent to Judge',
      'Build',
      'Run',
      'Resource Limit Check',
      'Result Check',
      'Presentation Check'
    ]

    const currentJudgeStep = computed(() => {
      switch (judgeRecord.value ? judgeRecord.value.status : '') {
        case 'WAITING':
          return judgeStepList[0]
        case 'COMPILEERROR':
        case 'RUNNING':
          return judgeStepList[2]
        case 'TIMELIMIT':
        case 'RUNTIMEERROR':
          return judgeStepList[3]
        case 'MEMORYLIMIT':
        case 'OUTPUTLIMIT':
          return judgeStepList[4]
        case 'WRONGANSWER':
          return judgeStepList[5]
        case 'PRESENTATIONERROR':
          return judgeStepList[6]
        case 'ACCEPTED':
          return 'ACCEPTED'
        default:
          return ''
      }
    })

    return {
      isLightMode,
      judgeStepList,
      currentJudgeStep,
      judgeRecord,
      isJudgeLoading
    }
  }
})
</script>

<style scoped>
.loading::after {
  content: '';
  position: absolute;
  left: 50%;

  width: 1.5em;
  height: 1.5em;
  border: 3px white solid;
  border-top: 3px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes circ {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-dark::after {
  content: '';
  position: absolute;
  left: 50%;

  width: 1.5em;
  height: 1.5em;
  border: 3px black solid;
  border-top: 3px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes circ {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
